import Vue from 'vue'
import {make} from 'vuex-pathify'
import {byId, crudifyMutations, resetResults, savePayload} from 'src/utilities/vuex2'
import {objectBy, objectById, searchFieldsHaveValues} from 'src/utilities/utils'
import {createAndDownloadWorksheet} from 'src/utilities/excel'
import Constants from 'src/utilities/constants'
import {booleanTrueQuery, dateBetween, esClient, filterBuilder, multiMatch} from 'src/utilities/ptracSearch'
import _ from 'lodash'
import {ptracGet, ptracGetEntities} from 'src/utilities/ptracApi'
import {OK} from 'http-status-codes'
import {hydratePriceRule} from '@/utilities/ptracApiHelper'

const edit = {
  id: null,
  shortId: null,
  price: 0,
  taskNameId: null,
  taskName: null,
  jobName: null,
  department: null,
  departmentId: null,
  builder: null,
  builderId: null,
  client: null,
  clientId: null,
  projectDefault: false, // isProjectDefaultRule
  clientDefault: false, // isClientDefaultRule
  location: false, // isLocationRule
  projectName: null,
  projectType: null,
  streetNumber: null,
  streetName: null,
  buildingNumber: null,
  floorNumber: null,
  suiteNumber: null,
  city: null,
  county: null,
  state: 'TX',
  jurisdiction: null,
  legalSubdivision: null,
  legalSubdivisionId: null,
  community: null,
  communityId: null,
  hourly: false,
  hourlyRate: 0,
  repeatedTask: false,
  expiry: null,
  qbItemCode: null,
  notes: '', // leave as empty string
  travelCharge: false,
  createdBy: null,
  updatedBy: null,
  lastUsed: null,
  createdAt: null,
  updatedAt: null,
}

const query = {
  searchText: null,
  shortId: null,
  departments: [],
  expiry: [],
  createdAt: [],
  updatedAt: [],
  communityId: null,
  clientId: null,
  taskName: null,
  jobName: null,
  projectType: null,
  projectName: null,
  hourly: false,
  repeatedTask: false,
  projectDefault: false,
  clientDefault: false,
  location: false,
}

const initialState = {
  edit: JSON.parse(JSON.stringify(edit)),
  tasksByJobId: [],
  results: [],
  query: JSON.parse(JSON.stringify(query)),
  tripCharge: false
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)
getters.byId = byId
getters.savePayload = savePayload

getters.tasksByJobId = (state) => {
  return state.tasksByJobId === undefined ? [] : state.tasksByJobId
}

getters.searchFieldsHaveValues = (state) => {
  const searchFields = [
    'searchText',
    'clientId',
    'communityId',
    'taskName',
    'jobName',
    'projectType',
    'projectName',
    'shortId',
    'departments',
    'expiry',
    'createdAt',
    'updatedAt'
  ]

  return searchFieldsHaveValues(state.query, searchFields)
}

const mutations = make.mutations(state)
crudifyMutations(mutations)

mutations.resetResults = resetResults

mutations.resetEdit = (state) => {
  state.edit = JSON.parse(JSON.stringify(edit))
}

mutations.resetQuery = (state) => {
  state.query = JSON.parse(JSON.stringify(initialState.query))
}

mutations.updatePriceRule = (state, priceRule) => {
  const prToUpdate = objectBy(state.results, '_id', priceRule.id)
  if (prToUpdate) {
    prToUpdate._source = hydratePriceRule(priceRule)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions: {
    async fetchSearchResults({state, commit, rootGetters}) {
      const searchBody = {
        size: Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS,
        query: {
          bool: {
            must: [],
            should: [],
            filter: []
          }
        }
      }
      const fields = [
        'price',
        'shortId',
        'hourlyRate',
        'taskName',
        'projectType',
        'projectName',
        'jobName',
        'department',
        'builder',
        'client',
        'buildingNumber',
        'floorNumber',
        'city',
        'state',
        'county',
        'jurisdiction',
        'legalSubdivision',
        'streetName',
        'streetNumber',
        'suiteNumber'
      ]
      const filterFields = [
        'taskName',
        'projectType',
        'projectName',
        'jobName',
        'communityId',
        'clientId',
        'shortId'
      ]

      if (state.query.searchText) {
        let searchTerms = state.query.searchText.toLowerCase().split(" ")

        searchBody.query.bool.should.push(multiMatch(fields, searchTerms))
        searchBody.query.bool.minimum_should_match = 1
      }
      // Departments
      if (state.query.departments.length > 0) {
        searchBody.query.bool.must.push({match: {department: state.query.departments.join(" ")}})
      }

      // Filters
      searchBody.query.bool.filter.push(...filterBuilder(filterFields, state.query))

      // Date filter
      if (state.query.expiry == null) {
        state.query.expiry = []
      }
      if (state.query.expiry.length > 0) {
        searchBody.query.bool.filter.push(dateBetween(state.query.expiry, Constants.ENTITY_FIELDS.PRICE_RULES.EXPIRY))
      }
      if (state.query.createdAt == null) {
        state.query.createdAt = []
      }
      if (state.query.createdAt.length > 0) {
        searchBody.query.bool.filter.push(dateBetween(state.query.createdAt, Constants.ENTITY_FIELDS.PRICE_RULES.CREATED_AT))
      }
      if (state.query.updatedAt == null) {
        state.query.updatedAt = []
      }
      if (state.query.updatedAt.length > 0) {
        searchBody.query.bool.filter.push(dateBetween(state.query.updatedAt, Constants.ENTITY_FIELDS.PRICE_RULES.UPDATED_AT))
      }
      if (state.query.hourly) {
        booleanTrueQuery('hourly', searchBody)
      }
      if (state.query.repeatedTask) {
        booleanTrueQuery('repeatedTask', searchBody)
      }
      if (state.query.projectDefault) {
        booleanTrueQuery('projectDefault', searchBody)
      }
      if (state.query.clientDefault) {
        booleanTrueQuery('clientDefault', searchBody)
      }
      if (state.query.location) {
        booleanTrueQuery('location', searchBody)
      }

      // console.log(JSON.stringify(searchBody, null, 2))

      const esCreds = rootGetters['config/byName'](Constants.CONFIGS.ELASTICSEARCH_UN_PW)
      let response = await esClient(esCreds).search({
          index: 'pricerules',
          body: searchBody
        })

      const priceRulesResults = response.hits.hits
      for (const priceRule of priceRulesResults) {
        priceRule._source = hydratePriceRule(priceRule._source)
      }

      commit('results', priceRulesResults)

      return response
    },
    resultsToExcel({state, rootGetters}) {
      const fileName = 'price-rules.xlsx'
      const tabName = 'Price Rules'
      const header = [{
        header: 'Short Id',
        key: 'shortId',
        width: 10
      }, {
        header: 'Price',
        key: 'price',
        width: 10
      }, {
        header: 'Hourly Rate',
        key: 'hourlyRate',
        width: 10
      }, {
        header: 'Hourly',
        key: 'hourly',
        width: 15
      }, {
        header: 'Task Name',
        key: 'taskName',
        width: 25
      }, {
        header: 'Project Type',
        key: 'projectType',
        width: 25
      }, {
        header: 'Job Name',
        key: 'jobName',
        width: 30
      }, {
        header: 'Department',
        key: 'department',
        width: 20
      }, {
        header: 'Builder',
        key: 'builder',
        width: 25
      }, {
        header: 'Client',
        key: 'client',
        width: 25
      }, {
        header: 'Project Name',
        key: 'projectName',
        width: 25
      }, {
        header: 'City',
        key: 'city',
        width: 15
      }, {
        header: 'County',
        key: 'county',
        width: 15
      }, {
        header: 'State',
        key: 'state',
        width: 10
      }, {
        header: 'Jurisdiction',
        key: 'jurisdiction',
        width: 20
      }, {
        header: 'Legal Subdivision',
        key: 'legalSubdivision',
        width: 20
      }, {
        header: 'Community',
        key: 'community',
        width: 20
      }, {
        header: 'Street Number',
        key: 'streetNumber',
        width: 15
      }, {
        header: 'Street Name',
        key: 'streetName',
        width: 20
      }, {
        header: 'Building Number',
        key: 'buildingNumber',
        width: 15
      }, {
        header: 'Floor Number',
        key: 'floorNumber',
        width: 15
        }, {
        header: 'Suite Number',
        key: 'suiteNumber',
        width: 15
      }, {
        header: 'Repeated Task',
        key: 'repeatedTask',
        width: 15
      }, {
        header: 'Expiry',
        key: 'expiry',
        width: 25
      }, {
        header: 'Item Code',
        key: 'qbItemCode',
        width: 25
      }, {
        header: 'Notes',
        key: 'notes',
        width: 20
      }, {
        header: 'Item created At',
        key: 'createdAt',
        width: 25
      }, {
        header: 'Created By',
        key: 'createdBy',
        width: 25
      }, {
        header: 'Item Updated At',
        key: 'updatedAt',
        width: 25
      }, {
        header: 'Last Updated By',
        key: 'updatedBy',
        width: 25
      }, {
        header: 'Last Used',
        key: 'lastUsed',
        width: 25
      }]
      const priceRules = _.map(state.results, '_source')

      const itemCodes = rootGetters['quickbooks/results']

      const users = rootGetters['users/results']

      const rows = []
      priceRules.forEach((priceRule) => {
        const itemCode = objectById(itemCodes, priceRule.qbItemCode)
        const userNameUpdated = objectById(users, priceRule.updatedBy)
        const userNameCreated = objectById(users, priceRule.createdBy)

        const rule = {
          price: priceRule.price,
          shortId: priceRule.shortId,
          hourlyRate: priceRule.hourlyRate,
          hourly: priceRule.hourly,
          taskName: priceRule.taskName,
          projectType: priceRule.projectType,
          jobName: priceRule.jobName,
          department: priceRule.department,
          builder: priceRule.builder,
          client: priceRule.client,
          projectName: priceRule.projectName,
          city: priceRule.city,
          county: priceRule.county,
          state: priceRule.state,
          jurisdiction: priceRule.jurisdiction,
          legalSubdivision: priceRule.legalSubdivision,
          community: priceRule.community,
          streetNumber: priceRule.streetNumber,
          streetName: priceRule.streetName,
          buildingNumber: priceRule.buildingNumber,
          floorNumber: priceRule.floorNumber,
          suiteNumber: priceRule.suiteNumber,
          reinspection: priceRule.reinspection,
          expiry: priceRule.expiry,
          notes: priceRule.notes,
          createdAt: priceRule.createdAt,
          updatedAt: priceRule.updatedAt,
          lastUsed: priceRule.lastUsed
        }

        if (itemCode) {
          rule.qbItemCode = itemCode.name
        }

        if (userNameUpdated) {
          rule.lastUpdatedByUser = userNameUpdated.name
        }

        if (userNameCreated) {
          rule.createdByUser = userNameCreated.name
        }

        rows.push(rule)
      })

      createAndDownloadWorksheet(fileName, tabName, header, rows, rootGetters['security/username'])
    },
    async fetchFindByCity({}, city) {
      return await ptracGetEntities('price-rules/search/findByCity?city=' + encodeURIComponent(city))
    },
    async fetchFindByLegalSubdivision({}, legalSubdivision) {
      return await ptracGetEntities('price-rules/search/findByLegalSubdivision?legalSubdivision=' + encodeURIComponent(legalSubdivision))
    },
    async fetchFindByBuilderIdOrClientId({}, companyId) {
      return await ptracGetEntities('price-rules/search/findByBuilderIdOrClientId?clientId=' + companyId + '&builderId=' + companyId)
    },
    async fetchFindByCommunityId({}, communityId) {
      return await ptracGetEntities('price-rules/search/findByCommunityId?communityId=' + communityId)
    },
    async fetchFindByStreetName({}, streetName) {
      return await ptracGetEntities('price-rules/search/findByStreetName?streetName=' + encodeURIComponent(streetName))
    },
    async fetchFindByTaskNameId({}, taskNameId) {
      return await ptracGetEntities('price-rules/search/findByTaskNameId?taskNameId=' + taskNameId)
    },
    async fetchPriceRules({commit}, payload) {
      let url = 'price-rules/find?jobId=' + payload.job.id

      if (payload.taskId) {
        url += '&taskId=' + payload.taskId
      }

      const response = await ptracGet(url)

      if (OK === response.status) {
        const priceRules = response.data

        if (Array.isArray(priceRules)) {
          priceRules.forEach((priceRule) => {
            for (const field in priceRule.matchedFields) {
              switch (field) {
                case 'builderId':
                  priceRule.matchedFields['builder'] = priceRule['builder']
                  delete priceRule.matchedFields.builderId
                  break
                case 'clientId':
                  priceRule.matchedFields['client'] = priceRule['client']
                  delete priceRule.matchedFields.clientId
                  break
                case 'communityId':
                  priceRule.matchedFields['community'] = priceRule['community']
                  delete priceRule.matchedFields.communityId
                  break
                case 'legalSubdivisionId':
                  priceRule.matchedFields['legalSubdivision'] = priceRule['legalSubdivision']
                  delete priceRule.matchedFields.legalSubdivisionId
                  break
              }
            }
          })
          commit('results', priceRules)
        }
      }

      return response
    }
  }
}
